/**
 * Merchant Detail Page — Full merchant profile with tabbed sections
 * Shows: Overview, Entities, Locations, Owners, Banking, Processing, Applications, Documents, Relationships
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Store, Building2, MapPin, CreditCard, Users, FileText, ArrowLeft, Plus,
  ChevronDown, ChevronRight, Clock, Check, AlertCircle, Landmark,
  Wallet, FileCheck, UserPlus, Shield
} from 'lucide-react';
import { displayProcessor } from '../lib/processorDisplay';
import RelationshipsAttributionTab from '../components/merchant/RelationshipsAttributionTab';
import StartBoardingDialog from '../components/boarding/StartBoardingDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'overview', label: 'Overview', icon: Store },
  { key: 'entities', label: 'Business Info', icon: Building2 },
  { key: 'locations', label: 'Locations', icon: MapPin },
  { key: 'banking', label: 'Banking', icon: Wallet },
  { key: 'processing', label: 'Processing', icon: CreditCard },
  { key: 'applications', label: 'Applications', icon: FileCheck },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'relationships', label: 'Relationships & Attribution', icon: Users },
];

const APP_TYPES = [
  { value: 'merchant_processing', label: 'Merchant Processing' },
  { value: 'pos', label: 'POS System' },
  { value: 'equipment_financing', label: 'Equipment Financing' },
  { value: 'lending', label: 'Lending' },
  { value: 'ach', label: 'ACH' },
  { value: 'hardware_lease', label: 'Hardware Lease' },
];

export default function MerchantDetailPage() {
  const { groupId } = useParams();
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showNewApp, setShowNewApp] = useState(false);
  const [showBoarding, setShowBoarding] = useState(false);
  const [newApp, setNewApp] = useState({ application_type: '', vendor_name: '', legal_entity_id: '', location_id: '' });
  const [creatingApp, setCreatingApp] = useState(false);
  const [appResult, setAppResult] = useState(null);

  const fetchGroup = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/merchants/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setGroup(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [groupId, token]);

  useEffect(() => { fetchGroup(); }, [fetchGroup]);

  const allLocations = (group?.entities || []).flatMap(e => (e.locations || []).map(l => ({ ...l, entity_name: e.legal_business_name })));
  const allOwners = (group?.entities || []).flatMap(e => (e.owners || []).map(o => ({ ...o, entity_name: e.legal_business_name })));
  const allBanks = (group?.entities || []).flatMap(e => (e.bank_accounts || []).map(b => ({ ...b, entity_name: e.legal_business_name })));
  const allProcessing = allLocations.flatMap(l => (l.processing_accounts || []).map(p => ({ ...p, location_dba: l.dba })));

  const handleStartApp = async () => {
    if (!newApp.legal_entity_id || !newApp.location_id || !newApp.application_type) return;
    setCreatingApp(true);
    try {
      const res = await fetch(`${API}/api/application-engine/applications`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchant_group_id: groupId, ...newApp }),
      });
      if (res.ok) {
        const data = await res.json();
        setAppResult(data);
      }
    } catch (e) { console.error(e); }
    finally { setCreatingApp(false); }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading merchant...</div>;
  if (!group) return <div className="text-center py-12 text-red-500">Merchant not found</div>;

  return (
    <div className="space-y-6" data-testid="merchant-detail-page">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/merchants')} data-testid="back-to-merchants">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{group.group_name}</h1>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mt-1">
            {group.industry && <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">{group.industry}</span>}
            {group.primary_contact && <span>{group.primary_contact}</span>}
            {group.primary_email && <span>{group.primary_email}</span>}
            <span className={`px-2 py-0.5 rounded-full text-xs ${group.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600'}`}>
              {group.status}
            </span>
          </div>
        </div>
        <Button data-testid="start-new-app-btn" onClick={() => setShowBoarding(true)} className="gap-2">
          <Plus className="w-4 h-4" /> Start New Application
        </Button>
      </div>

      {showBoarding && <StartBoardingDialog groupId={groupId} open onClose={() => setShowBoarding(false)} />}
      {/* Start New Application Panel */}
      {showNewApp && !appResult && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6" data-testid="new-app-panel">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Start New Application</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Application Type *</label>
              <select
                data-testid="app-type-select"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                value={newApp.application_type}
                onChange={(e) => setNewApp(a => ({ ...a, application_type: e.target.value }))}
              >
                <option value="">Select type...</option>
                {APP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Legal Entity *</label>
              <select
                data-testid="entity-select"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                value={newApp.legal_entity_id}
                onChange={(e) => {
                  setNewApp(a => ({ ...a, legal_entity_id: e.target.value, location_id: '' }));
                }}
              >
                <option value="">Select entity...</option>
                {(group.entities || []).map(e => (
                  <option key={e.id} value={e.id}>
                    {e.legal_business_name} {e.ein ? `(EIN: ${e.ein})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Location *</label>
              <select
                data-testid="location-select"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                value={newApp.location_id}
                onChange={(e) => setNewApp(a => ({ ...a, location_id: e.target.value }))}
              >
                <option value="">Select location...</option>
                {(group.entities || [])
                  .filter(e => e.id === newApp.legal_entity_id)
                  .flatMap(e => e.locations || [])
                  .map(l => (
                    <option key={l.id} value={l.id}>
                      {l.dba || 'Location'} — {l.physical_address?.city}, {l.physical_address?.state}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Vendor</label>
              <Input
                data-testid="vendor-input"
                placeholder="e.g. Fiserv, ABC Finance"
                value={newApp.vendor_name}
                onChange={(e) => setNewApp(a => ({ ...a, vendor_name: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              data-testid="create-app-btn"
              onClick={handleStartApp}
              disabled={creatingApp || !newApp.legal_entity_id || !newApp.location_id || !newApp.application_type}
            >
              {creatingApp ? 'Creating...' : 'Create Application'}
            </Button>
            <Button variant="outline" onClick={() => setShowNewApp(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Application Created Result */}
      {appResult && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6" data-testid="app-result-panel">
          <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center gap-2">
            <Check className="w-5 h-5" /> Application Created
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            {appResult.canonical_fields_populated} of {appResult.canonical_fields_total} fields pre-filled from Merchant Vault
          </p>
          {/* Readiness */}
          <div className="space-y-1 mb-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">APPLICATION READINESS</p>
            {Object.entries(appResult.readiness?.sections || {}).map(([section, status]) => (
              <div key={section} className="flex items-center gap-2 text-sm">
                {status === 'complete' ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                )}
                <span className="text-gray-700 dark:text-gray-300 capitalize">{section.replace(/_/g, ' ')}</span>
                <span className={`text-xs ${status === 'complete' ? 'text-green-600' : 'text-amber-600'}`}>{status}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate(`/applications/${appResult.application?.id}`)} data-testid="go-to-app-btn">
              Open Application
            </Button>
            <Button variant="outline" onClick={() => { setAppResult(null); setShowNewApp(false); }}>Close</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <div className="flex gap-1">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  tab === t.key
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {tab === 'overview' && <OverviewTab group={group} entities={group.entities} locations={allLocations} processing={allProcessing} />}
        {tab === 'entities' && <EntitiesTab entities={group.entities || []} />}
        {tab === 'locations' && <LocationsTab locations={allLocations} />}
        {tab === 'banking' && <BankingTab banks={allBanks} />}
        {tab === 'processing' && <ProcessingTab accounts={allProcessing} />}
        {tab === 'applications' && <ApplicationsTab applications={group.applications || []} navigate={navigate} />}
        {tab === 'documents' && <DocumentsTab documents={group.documents || []} />}
        {tab === 'relationships' && <RelationshipsAttributionTab groupId={groupId} />}
      </div>
    </div>
  );
}

// ── Tab Components ───────────────────────────────────────

function OverviewTab({ group, entities, locations, processing }) {
  const originatingAgent = (group?.relationships || []).find(r => r.role === 'originating_agent');
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <StatCard icon={Building2} label="Legal Entities" value={entities?.length || 0} color="blue" />
      <StatCard icon={MapPin} label="Locations" value={locations?.length || 0} color="green" />
      <StatCard icon={CreditCard} label="Processing Accounts" value={processing?.length || 0} color="purple" />
      {originatingAgent && (
        <div className="col-span-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center gap-3" data-testid="originating-agent-block">
          <UserPlus className="w-5 h-5 text-blue-500" />
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Originating Agent</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{originatingAgent.user?.name || 'Unknown'}</p>
          </div>
        </div>
      )}
      <div className="col-span-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Merchant Hierarchy</h3>
        {(entities || []).map(entity => (
          <div key={entity.id} className="mb-3 last:mb-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
              <Building2 className="w-4 h-4 text-blue-500" />
              {entity.legal_business_name}
              {entity.ein && <span className="text-xs text-gray-500">(EIN: {entity.ein})</span>}
            </div>
            {(entity.locations || []).map(loc => (
              <div key={loc.id} className="ml-6 mt-1">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <MapPin className="w-3.5 h-3.5 text-green-500" />
                  {loc.dba || 'Location'} — {loc.physical_address?.city}, {loc.physical_address?.state}
                </div>
                {(loc.processing_accounts || []).map(pa => (
                  <div key={pa.id} className="ml-6 mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                    <CreditCard className="w-3 h-3 text-purple-500" />
                    {displayProcessor(pa.processor)} — MID: {pa.mid || 'Pending'}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${pa.processing_status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                      {pa.processing_status}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = { blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400', green: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400', purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' };
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </div>
  );
}

function EntitiesTab({ entities }) {
  return (
    <div className="space-y-4">
      {entities.map(e => (
        <div key={e.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">{e.legal_business_name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${e.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600'}`}>{e.status}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="DBA" value={e.dba} />
            <Field label="EIN" value={e.ein} />
            <Field label="Entity Type" value={e.entity_type} />
            <Field label="State" value={e.state_of_formation} />
            <Field label="Phone" value={e.business_phone} />
            <Field label="Email" value={e.business_email} />
            <Field label="MCC" value={e.mcc} />
            <Field label="Industry" value={e.industry} />
          </div>
          {e.corporate_address && Object.keys(e.corporate_address).length > 0 && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">Address:</span> {[e.corporate_address.street, e.corporate_address.city, e.corporate_address.state, e.corporate_address.zip].filter(Boolean).join(', ')}
            </div>
          )}
          {(e.owners || []).length > 0 && (
            <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase">Owners</p>
              {e.owners.map(o => (
                <div key={o.id} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 mb-1">
                  <Users className="w-3.5 h-3.5 text-gray-400" />
                  {o.first_name} {o.last_name} — {o.title || 'Owner'} ({o.ownership_percentage}%)
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${o.verification_status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {o.verification_status || 'entered'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {entities.length === 0 && <EmptyState icon={Building2} text="No legal entities yet" />}
    </div>
  );
}

function LocationsTab({ locations }) {
  return (
    <div className="space-y-3">
      {locations.map(l => (
        <div key={l.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 dark:text-white">{l.dba || 'Location'}</h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">{l.entity_name}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Address" value={l.physical_address ? [l.physical_address.street, l.physical_address.city, l.physical_address.state, l.physical_address.zip].filter(Boolean).join(', ') : ''} />
            <Field label="Phone" value={l.phone} />
            <Field label="Monthly Volume" value={l.monthly_volume ? `$${l.monthly_volume.toLocaleString()}` : ''} />
            <Field label="Avg Ticket" value={l.average_ticket ? `$${l.average_ticket}` : ''} />
            <Field label="Current Processor" value={l.current_processor} />
            <Field label="Current POS" value={l.current_pos} />
            <Field label="Store #" value={l.store_number} />
          </div>
        </div>
      ))}
      {locations.length === 0 && <EmptyState icon={MapPin} text="No locations yet" />}
    </div>
  );
}

function BankingTab({ banks }) {
  return (
    <div className="space-y-3">
      {banks.map(b => (
        <div key={b.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-blue-500" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{b.bank_name} — {b.account_display}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {b.account_type} | {b.entity_name}
                {b.has_vault_token && <span className="ml-2 text-green-600 dark:text-green-400">Vault tokenized</span>}
                {b.is_primary && <span className="ml-2 text-blue-600 dark:text-blue-400">Primary</span>}
              </p>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${b.verification_status === 'verified' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
            {b.verification_status || 'entered'}
          </span>
        </div>
      ))}
      {banks.length === 0 && <EmptyState icon={Wallet} text="No bank accounts yet" />}
    </div>
  );
}

function ProcessingTab({ accounts }) {
  return (
    <div className="space-y-3">
      {accounts.map(p => (
        <div key={p.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">{displayProcessor(p.processor)}</h3>
              {p.mid && <span className="text-sm text-gray-500 dark:text-gray-400">MID: {p.mid}</span>}
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${p.processing_status === 'active' ? 'bg-green-100 text-green-700' : p.processing_status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
              {p.processing_status}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Platform" value={displayProcessor(p.platform)} />
            <Field label="Pricing" value={p.pricing_program} />
            <Field label="IC Markup" value={p.interchange_markup ? `${p.interchange_markup}%` : ''} />
            <Field label="Txn Fee" value={p.transaction_fee ? `$${p.transaction_fee}` : ''} />
            <Field label="Monthly Fee" value={p.monthly_fee ? `$${p.monthly_fee}` : ''} />
            <Field label="Location" value={p.location_dba} />
          </div>
        </div>
      ))}
      {accounts.length === 0 && <EmptyState icon={CreditCard} text="No processing accounts yet" />}
    </div>
  );
}

function ApplicationsTab({ applications, navigate }) {
  return (
    <div className="space-y-3">
      {applications.map(a => (
        <div
          key={a.id}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-all"
          onClick={() => navigate(a.provider_account_id ? `/boarding/${a.id}` : `/applications/${a.id}`)}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {a.application_type ? a.application_type.replace(/_/g, ' ') : 'Application'}
                {a.vendor_name && ` — ${a.vendor_name}`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {a.business_name} | Created: {new Date(a.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded-full ${
                a.status === 'submitted' || a.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                a.status === 'draft' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}>{a.status}</span>
              {a.latest_snapshot_version && (
                <span className="text-[10px] text-gray-400">v{a.latest_snapshot_version}</span>
              )}
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>
      ))}
      {applications.length === 0 && <EmptyState icon={FileCheck} text="No applications yet — click 'Start New Application' above" />}
    </div>
  );
}

function DocumentsTab({ documents }) {
  return (
    <div className="space-y-3">
      {documents.map(d => (
        <div key={d.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{d.file_name}</p>
              <p className="text-xs text-gray-500">{d.document_type} | {new Date(d.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded ${d.verification_status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {d.verification_status || d.status}
          </span>
        </div>
      ))}
      {documents.length === 0 && <EmptyState icon={FileText} text="No documents uploaded yet" />}
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm text-gray-800 dark:text-gray-200">{value}</p>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-8">
      <Icon className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
      <p className="text-sm text-gray-500 dark:text-gray-400">{text}</p>
    </div>
  );
}
